import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { take, catchError } from 'rxjs/operators';
import moment from 'moment';
import { Product } from 'src/app/order/models/product.model';
import { ProductService } from 'src/app/quoting/services/product.service';
import { Warehouse } from '../../models/warehouse.model';
import { WarehouseLocation } from '../../models/warehouse-location.model';
import { ProductStockMovementReference } from '../../models/product-stock-movement-reference.model';
import { ProductStockMovement, MOVEMENT_TYPE_LABELS } from '../../models/product-stock-movement.model';
import { WarehouseService } from '../../services/warehouse.service';
import { WarehouseLocationService } from '../../services/warehouse-location.service';
import { StockMovementReferenceService } from '../../services/stock-movement-reference.service';
import { StockQuantityService } from '../../services/stock-quantity.service';
import { ReportCsvService } from '../../services/report-csv.service';
import { WarehousingReportService, WarehousingReportFilters } from '../../services/warehousing-report.service';
import { StockWeightService } from '../../services/stock-weight.service';

/**
 * The audit trail over the append-only ledger.
 *
 * Nothing on this screen edits or deletes: a movement is a fact that happened, and the
 * only correction is a further movement. That is why there are no row buttons.
 */
@Component({
    selector: 'app-report-movement-history',
    templateUrl: './report-movement-history.component.html',
    styleUrls: ['./report-movement-history.component.scss'],
    standalone: false
})
export class ReportMovementHistoryComponent implements OnInit {

  searchFilters: WarehousingReportFilters = new WarehousingReportFilters();

  warehouses: Warehouse[] = [];
  locations: WarehouseLocation[] = [];
  products: Product[] = [];
  references: ProductStockMovementReference[] = [];

  movementTypes: any[] = MOVEMENT_TYPE_LABELS;

  movements: ProductStockMovement[] = [];

  hasRun: boolean = false;
  running: boolean = false;

  constructor(private warehousingReportService: WarehousingReportService,
    private warehouseService: WarehouseService,
    private warehouseLocationService: WarehouseLocationService,
    private stockMovementReferenceService: StockMovementReferenceService,
    private productService: ProductService,
    private stockQuantityService: StockQuantityService,
    private stockWeightService: StockWeightService,
    private reportCsvService: ReportCsvService,
    private route: ActivatedRoute) { }

  ngOnInit(): void {
    this.warehouseService.getAllWarehouses()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load warehouses');
      return e;
    }))
    .subscribe((data: Warehouse[]) => {
      this.warehouses = data;
    })

    this.productService.getAllActiveProducts()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load products');
      return e;
    }))
    .subscribe((data: Product[]) => {
      this.products = data;
    })

    this.stockMovementReferenceService.getAllStockMovementReferences()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load movement references');
      return e;
    }))
    .subscribe((data: ProductStockMovementReference[]) => {
      this.references = data;
    })

    // The dashboard's negative-stock callout and the stock-on-hand report both link here
    // for one product, so the trail opens already run and scoped to it.
    this.route.queryParams
    .pipe(take(1))
    .subscribe((params) => {
      if (params['productId']) {
        this.searchFilters.productId = +params['productId'];
        this.loadReport();
      }
    })
  }

  /**
   * Locations are scoped by warehouseId and the same aisle/shelf can exist at two sites,
   * so the location list only becomes meaningful once a warehouse is chosen.
   */
  onWarehouseChanged(warehouseId: any) {
    this.searchFilters.warehouseId = +warehouseId;
    this.searchFilters.warehouseLocationId = -1;
    this.locations = [];

    if (this.searchFilters.warehouseId === -1) {
      return;
    }

    this.warehouseLocationService.getWarehouseLocationsForWarehouse(this.searchFilters.warehouseId)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load warehouse locations');
      return e;
    }))
    .subscribe((data: WarehouseLocation[]) => {
      this.locations = data;
    })
  }

  /** True while the date range is the wrong way round, for `is-invalid` on both inputs. */
  get datesInvalid(): boolean {
    if (!this.searchFilters.startDate || !this.searchFilters.endDate) {
      return false;
    }

    return moment(this.searchFilters.endDate).isBefore(moment(this.searchFilters.startDate));
  }

  loadReport() {
    if (this.datesInvalid) {
      return;
    }

    this.running = true;

    this.warehousingReportService.getMovements(this.searchFilters)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      this.running = false;
      alert('could not get report');
      return e;
    }))
    .subscribe((data: ProductStockMovement[]) => {
      this.movements = data ? data : [];
      this.hasRun = true;
      this.running = false;
    })
  }

  /**
   * A movement qty is signed, so this net is the effect the whole result set had on stock
   * rather than a volume. Transfers net to zero across a pair of rows, which is correct.
   */
  getNetQty(): string {
    const total: number = this.movements.reduce((sum: number, m: ProductStockMovement) => sum + Number(m.qty), 0);

    return this.stockQuantityService.round(total).toString();
  }

  /**
   * Net weight over the result set, or blank where nothing in it was weighed at both ends.
   *
   * Blank and not 0.000 for an unweighed set: a zero would state that the goods were weighed and had
   * not changed. The second row of a transfer is left out, because a transfer is one weighing written
   * onto two rows and both carry the same readings — unlike `getNetQty` above, where the pair carries
   * opposite signs and cancels itself. `StockWeightService` owns that rule; this screen does not
   * re-state it.
   *
   * Never rendered without `getUnweighedMovements()` beside it.
   */
  getNetWeight(): string {
    return this.stockWeightService.format(this.stockWeightService.netTotal(this.movements));
  }

  /** How many rows the net weight is built from. */
  getWeighedMovements(): number {
    return this.stockWeightService.weighed(this.movements);
  }

  /**
   * How many rows the net weight does NOT cover, drawn even when it is zero.
   *
   * Most movements are never weighed, so the total on its own is a real number that says nothing
   * about how much of the report it covers. Zero is the case worth rendering rather than the case
   * worth hiding: it is the only state in which the total covers everything, and a figure that
   * appears only when there is a problem cannot be told from a figure somebody forgot to draw.
   */
  getUnweighedMovements(): number {
    return this.stockWeightService.unweighed(this.movements);
  }

  /** Blank on a movement that was not weighed. A weight of 0 is a reading and renders as 0. */
  formatWeight(weight: number): string {
    return this.stockWeightService.format(weight);
  }

  /** Blank unless the movement was weighed at both ends — a single reading has no net. */
  formatNetWeight(movement: ProductStockMovement): string {
    return this.stockWeightService.formatNet(movement);
  }

  getMovementTypeLabel(movementType: string): string {
    const type: any = this.movementTypes.filter(t => t.value === movementType)[0];

    return type ? type.label : movementType;
  }

  isNegative(qty: number): boolean {
    return this.stockQuantityService.isNegative(qty);
  }

  formatQty(qty: number): string {
    return this.stockQuantityService.round(qty).toString();
  }

  formatDateTime(value: string): string {
    return value ? moment(value).format('DD/MM/YYYY HH:mm:ss') : '';
  }

  warehouseNameOf(movement: ProductStockMovement): string {
    return movement.warehouseLocation && movement.warehouseLocation.warehouse
      ? movement.warehouseLocation.warehouse.name : '';
  }

  exportToCSV() {
    // An unweighed movement exports as an empty cell, not as 0. A spreadsheet sums a column of
    // zeroes without complaint, which is how an unweighed set becomes a total somebody quotes.
    const headers: string[] = ['Id', 'Date', 'Product', 'SKU', 'Warehouse', 'Aisle', 'Shelf',
      'Type', 'Qty', 'Start Weight', 'End Weight', 'Net Weight',
      'Reference', 'Reference Code', 'Created By'];

    const rows: any[][] = this.movements.map((movement: ProductStockMovement) => [
      movement.id,
      this.formatDateTime(movement.createdAt),
      movement.product ? movement.product.name : '',
      movement.product ? movement.product.sku : '',
      this.warehouseNameOf(movement),
      movement.warehouseLocation ? movement.warehouseLocation.aisle : '',
      movement.warehouseLocation ? movement.warehouseLocation.shelf : '',
      this.getMovementTypeLabel(movement.movementType),
      this.formatQty(movement.qty),
      this.formatWeight(movement.startWeight),
      this.formatWeight(movement.endWeight),
      this.formatNetWeight(movement),
      movement.reference ? movement.reference.name : '',
      movement.reference ? movement.reference.code : '',
      movement.createdBy
    ]);

    this.reportCsvService.download('stock-movement-history', headers, rows);
  }
}
